import { useEffect, useState } from 'react';
import { Search, X, User, GraduationCap, FileText, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { aiAPI } from '@/lib/api';

const ICONS = { lead: User, student: GraduationCap, application: FileText };
const LABELS = { lead: 'Lead', student: 'Student', application: 'Application' };

export const RecordPicker = ({ value, onChange, testId = 'record-picker', dropUp = false }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return undefined; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await aiAPI.searchRecords(q);
        setResults(data);
      } catch { setResults([]); }
      setLoading(false);
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  if (value) {
    const Icon = ICONS[value.record_type] || User;
    return (
      <div data-testid={`${testId}-selected`} className="flex items-center gap-2 rounded-lg border border-teal-200 bg-teal-50 dark:bg-teal-900/30 dark:border-teal-800 px-3 py-2">
        <Icon className="w-4 h-4 text-teal-700 dark:text-teal-300 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-800 dark:text-white truncate">{value.name}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{LABELS[value.record_type]} · {value.subtitle}</p>
        </div>
        <button type="button" data-testid={`${testId}-clear`} onClick={() => onChange(null)} className="p-1 rounded hover:bg-teal-100 dark:hover:bg-teal-800">
          <X className="w-4 h-4 text-gray-500" />
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
      <Input
        data-testid={`${testId}-input`}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search lead, student or application…"
        className="pl-9"
      />
      {loading && <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-teal-600" />}
      {results.length > 0 && (
        <div data-testid={`${testId}-results`} className={`absolute z-30 w-full ${dropUp ? 'bottom-full mb-1' : 'mt-1'} max-h-64 overflow-y-auto rounded-lg border bg-white dark:bg-slate-800 dark:border-slate-600 shadow-lg`}>
          {results.map((r) => {
            const Icon = ICONS[r.record_type] || User;
            return (
              <button
                type="button"
                key={`${r.record_type}-${r.id}`}
                data-testid={`${testId}-option-${r.id}`}
                onClick={() => { onChange(r); setQ(''); setResults([]); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-teal-50 dark:hover:bg-slate-700 transition-colors"
              >
                <Icon className="w-4 h-4 text-teal-600 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm text-gray-800 dark:text-white truncate">{r.name || 'Unnamed'}</p>
                  <p className="text-xs text-gray-500 truncate">{LABELS[r.record_type]} · {r.subtitle}</p>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
